import { useCallback } from 'react';
import type { ProfileMakeItem } from 'fym-shared/src/music24/artistProfile';
import { retryItemOf } from '../profileAssets';
import type { ProfileSlot } from '../profileSlots';
import { profileToolOfKind, type ProfileBannerKind, type ProfileTool } from '../profileRoute';
import type { ProfileMake } from '../useProfileMake';

/**
 * "Try again" of a card that failed (design a3.4): the failed versions again with the same picture
 * and look (`retryItemOf`). A version with no id that its source needs, or whose picture is gone,
 * opens its tool instead. The press crops make again as one item.
 */
export function useProfileRetry(
  make: ProfileMake,
  openTool: (tool: ProfileTool, banner?: ProfileBannerKind) => void
): (slots: readonly ProfileSlot[]) => void {
  return useCallback(
    (slots: readonly ProfileSlot[]) => {
      const failed = slots.flatMap((slot) => (slot.failed === null ? [] : [slot.failed]));
      const first = failed[0];
      if (first === undefined) {
        return;
      }
      const items = failed.map(retryItemOf);
      if (items.some((item) => item === null)) {
        const tool = profileToolOfKind(first.kind);
        openTool(tool, tool === 'banners' ? (first.kind as ProfileBannerKind) : undefined);
        return;
      }
      // The three press crops share one make: one item with no variant.
      const unique = new Map<string, ProfileMakeItem>();
      (items as ProfileMakeItem[]).forEach((item) => unique.set(`${item.kind}.${item.variant ?? '*'}`, item));
      make.make([...unique.values()]).catch(console.error);
    },
    [make, openTool]
  );
}
